import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Quote,
  Users, ShieldCheck, Cpu, Award, Globe, Scale, Leaf,
  Heart, Sparkles, Lightbulb,
  TrendingUp, Network, BarChart3, Building2,
  ScrollText, FileSignature, Landmark,
} from "lucide-react";
import { ConsultationDialog } from "../components/ConsultationDialog";

export default function HomePage() {
  return (
    <main data-testid="home-page" className="bg-[#FBF6EC]">
      {/* ───────────── HERO ───────────── */}
      <section
        data-testid="hero-section"
        className="relative pt-32 lg:pt-40 pb-32 lg:pb-44 bg-[#2A0A0E] overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 bg-cover bg-center hero-kb hero-kb-1"
            style={{ backgroundImage: "url('/hero-ladder.jpg')" }}
          />
          <div
            className="absolute inset-0 bg-cover bg-center hero-kb hero-kb-2"
            style={{ backgroundImage: "url('/hero-jungle.jpg')" }}
          />
          <div
            className="absolute inset-0 bg-cover bg-center hero-kb hero-kb-3"
            style={{ backgroundImage: "url('/hero-ladder.jpg')" }}
          />
          {/* Maroon overlay — gradient left→right (dark to white/transparent), fades out after 5s */}
          <div className="absolute inset-0 hero-overlay-fade bg-gradient-to-r from-[#2A0A0E] via-[#2A0A0E]/70 to-transparent" />

          {/* Moving maroon structures */}
          <div className="absolute top-[10%] left-[8%] w-[420px] h-[420px] rounded-full bg-[#7A1F26]/35 blur-3xl float-orb-1" />
          <div className="absolute bottom-[15%] right-[5%] w-[520px] h-[520px] rounded-full bg-[#C9A961]/18 blur-3xl float-orb-2" />
          <div className="absolute top-[40%] right-[25%] w-[360px] h-[360px] rounded-full bg-[#A02828]/25 blur-3xl float-orb-3" />
          <div className="absolute top-[18%] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A961]/40 to-transparent drift-line" />
          <div className="absolute bottom-[28%] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A961]/30 to-transparent drift-line delay-7" />
        </div>

        <div className="relative container-myc">
          <div className="max-w-4xl animate-fadeUp">
            <span className="inline-flex items-center text-[#C9A961] text-[11px] font-medium tracking-[0.35em] uppercase">
              Wealth · Advisory · Legacy
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] xl:text-[72px] leading-[1.06] text-[#FBF6EC] mt-7 font-light">
              A <span className="italic text-[#C9A961]">360° wealth approach</span>
              <br />designed to prepare you for <span className="italic">what matters most.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-[#FBF6EC]/85 leading-relaxed max-w-2xl font-light">
              Trust, integrity, and transparency are the foundations of our wealth advisory platform — guiding us to act in the best interests of our clients every day.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fadeUp delay-200">
              <Link
                to="/contact"
                data-testid="hero-get-started-button"
                className="inline-flex items-center justify-center gap-2 bg-[#C9A961] text-[#2A0A0E] hover:bg-[#FBF6EC] transition-all duration-300 px-8 py-4 font-medium text-sm tracking-[0.2em] uppercase"
              >
                Begin the Conversation <ArrowRight className="w-4 h-4" />
              </Link>
              <ConsultationDialog
                trigger={
                  <button
                    data-testid="hero-book-consultation-button"
                    className="inline-flex items-center justify-center gap-2 border border-[#FBF6EC]/40 text-[#FBF6EC] hover:bg-[#FBF6EC] hover:text-[#2A0A0E] transition-all duration-300 px-8 py-4 font-medium text-sm tracking-[0.2em] uppercase"
                  >
                    Book Consultation
                  </button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── 15 YEARS / INTRO ───────────── */}
      <section data-testid="intro-section" className="py-24 lg:py-32 relative bg-white">
        <div className="container-myc grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">About Overview</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              <span className="text-[#7A1F26] italic">15 years</span> of redefining excellence in wealth management and beyond.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-[17px] text-[#2A0A0E]/85 leading-[1.75] font-light">
              <span className="font-semibold text-[#7A1F26]">MONEYYARD CAPITAL</span> is an independent multi-family office and wealth advisory firm serving prominent Indian business families through offices across India.
            </p>
            <p className="text-[17px] text-[#2A0A0E]/85 leading-[1.75] font-light mt-5">
              Built on trust, integrity, and transparency, the firm pioneered the concept of unbiased investment advisory in India's wealth management industry. MONEYYARD was established to provide holistic, conflict-free advisory services to UHNI and HNI clients.
            </p>
            <Link
              to="/about"
              data-testid="intro-learn-more-link"
              className="inline-flex items-center gap-2 mt-10 text-[#7A1F26] hover:gap-3 transition-all text-sm tracking-[0.2em] uppercase font-medium"
            >
              About our firm <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────── DIFFERENTIATORS (6) ───────────── */}
      <section data-testid="differentiators-section" className="py-24 lg:py-32 bg-[#FAF0DC] relative">
        <div className="container-myc">
          <div className="max-w-3xl">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Our Approach</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              Where <span className="italic text-[#7A1F26]">expertise</span> meets your financial ambitions.
            </h2>
            <p className="text-[17px] text-[#2A0A0E]/80 leading-[1.75] font-light mt-6 max-w-2xl">
              We provide advice rooted in absolute integrity and driven solely by our clients' best interests.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A0A0E]/[0.08] mt-16 border border-[#2A0A0E]/[0.08]">
            {[
              { icon: Users, t: "Hyper-personalisation", d: "Working with ultra-high-net-worth families has reinforced our belief that every family requires a bespoke and highly personalised wealth advisory approach." },
              { icon: ShieldCheck, t: "0% Conflict", d: "As one of India's leading multi-family offices, our recommendations are guided solely by our clients' best interests — not by commissions from product manufacturers." },
              { icon: Cpu, t: "Technology Led", d: "Digital transformation and automation are at the core of our operations. We leverage advanced AI-driven innovations to serve our clients efficiently and with a high degree of accuracy." },
              { icon: Award, t: "Domain Expertise", d: "The extensive industry expertise accumulated by our seasoned professionals enables us to deliver highly bespoke, strategic, and value-driven insights to our clients." },
              { icon: Globe, t: "Open Architecture", d: "Our advisory framework provides clients with unrestricted access to the global investment landscape — to evaluate, analyse, and capitalise on a comprehensive spectrum of opportunities." },
              { icon: Scale, t: "Strictly Allocators", d: "Our operating framework enables us to assess products and strategies exclusively on their intrinsic merit. Advice driven by objectivity serves our clients' best interests." },
            ].map(({ icon: Icon, t, d }, i) => (
              <div
                key={t}
                data-testid={`differentiator-${i}`}
                className="bg-[#FBF6EC] p-9 lg:p-10 hover:bg-[#FAF5EB] transition-colors group"
              >
                <div className="w-12 h-12 inline-flex items-center justify-center border border-[#7A1F26]/30 group-hover:bg-[#7A1F26] group-hover:border-[#7A1F26] transition-all">
                  <Icon className="w-5 h-5 text-[#7A1F26] group-hover:text-[#FBF6EC] transition-colors" />
                </div>
                <h3 className="font-display text-2xl text-[#2A0A0E] mt-7 font-medium">{t}</h3>
                <p className="text-[15px] text-[#2A0A0E]/70 leading-[1.7] mt-3 font-light">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── INVESTMENT SOLUTIONS (12) ───────────── */}
      <section data-testid="investment-solutions-section" className="py-24 lg:py-32 bg-white relative">
        <div className="container-myc">
          <div className="max-w-3xl">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Investment Solutions</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              A complete <span className="italic text-[#7A1F26]">solutions architecture</span> for every mandate.
            </h2>
            <p className="text-[17px] text-[#2A0A0E]/80 leading-[1.75] font-light mt-6 max-w-2xl">
              From regulated investment products to bespoke wealth-transfer structures — every solution is delivered with the same fiduciary standard.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A0A0E]/[0.08] mt-16 border border-[#2A0A0E]/[0.08]">
            {[
              "Mutual Funds",
              "Stocks & Demat Accounts",
              "NRI Investment Services",
              "PMS & AIF",
              "Structured Investment Funds (SIF)",
              "GIFT City Funds",
              "Corporate FDs & Bonds",
              "Treasury Management",
              "Wills & Estate Planning",
              "Portfolio Restructuring",
              "Retirement Planning",
              "Succession Planning",
            ].map((label, i) => (
              <div
                key={label}
                data-testid={`investment-solution-${i}`}
                className="bg-white p-8 hover:bg-[#FAF5EB] transition-colors group"
              >
                <p className="font-display text-[15px] tracking-[0.05em] text-[#C9A961] font-medium">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-display text-xl lg:text-2xl text-[#2A0A0E] mt-4 font-medium leading-tight group-hover:text-[#7A1F26] transition-colors">
                  {label}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── CLIENT SEGMENTS (5) ───────────── */}
      <section data-testid="client-segments-section" className="py-24 lg:py-32 bg-[#FAF0DC] relative">
        <div className="container-myc">
          <div className="max-w-3xl">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Client Segments</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              Wealth advisory <span className="italic text-[#7A1F26]">tailored</span> to your reality.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-16">
            {[
              {
                title: "Ultra HNI & Family Businesses",
                asset: "Investable assets > INR 100 Cr",
                desc: "Comprehensive wealth advisory and strategic planning for multi-generational family businesses and ultra-high-net-worth families.",
                services: ["Family Office Advisory", "Succession Planning", "Governance Structuring", "Philanthropy Advisory", "Strategic Investment Planning", "Legacy & Estate Structuring"],
              },
              {
                title: "Global Indians",
                asset: "Investable assets > INR 5 Cr",
                desc: "Strategic investment advisory for global Indian families seeking structured access to Indian markets and long-term preservation.",
                services: ["India-focused Investment Advisory", "Estate Planning & Wealth Transfer", "Philanthropy & Impact Investing", "Analytics & Performance Reports", "Cross-border Wealth Structuring", "Tax-efficient Planning"],
              },
              {
                title: "Professionals & CXOs",
                asset: "Investable assets > INR 10 Cr",
                desc: "Tailored wealth management for senior professionals, business leaders, and corporate executives navigating complex responsibilities.",
                services: ["Investment Advisory", "Retirement Planning", "Portfolio Restructuring", "Estate Planning", "Wealth Preservation Strategies", "Executive Compensation Planning"],
              },
              {
                title: "Media, Sports & Entertainment",
                asset: "Investable assets > INR 5 Cr",
                desc: "Wealth advisory tailored to the unique and evolving needs of professionals in the entertainment, media, and sports ecosystem.",
                services: ["Wealth Preservation", "Brand & Royalty Structuring", "Cash Flow Planning", "Tax-efficient Investing", "Global Asset Diversification", "Legacy Planning"],
              },
              {
                title: "Founders & Entrepreneurs",
                asset: "Investable assets > INR 5 Cr",
                desc: "Comprehensive personal wealth planning for India's notable startup founders, from pre-liquidity to post-exit stages.",
                services: ["Pre & Post Exit Planning", "Concentrated Equity Management", "Treasury Solutions", "Family Office Structuring", "Risk & Liquidity Planning", "Succession & Legacy Advisory"],
              },
            ].map((seg, i) => (
              <div
                key={seg.title}
                data-testid={`client-segment-${i}`}
                className="bg-white p-9 lg:p-10 border border-[#2A0A0E]/8 hover:shadow-xl transition-shadow"
              >
                <h3 className="font-display text-2xl lg:text-[28px] text-[#2A0A0E] font-medium leading-tight">
                  {seg.title}
                </h3>
                <p className="text-[11px] tracking-[0.25em] uppercase text-[#C9A961] mt-3 font-medium">
                  {seg.asset}
                </p>
                <p className="text-[15px] text-[#2A0A0E]/75 leading-[1.7] mt-5 font-light">
                  {seg.desc}
                </p>
                <div className="mt-6 pt-5 border-t border-[#2A0A0E]/10">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#7A1F26] font-semibold mb-3">Services</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {seg.services.map((s) => (
                      <li key={s} className="text-[13px] text-[#2A0A0E]/75 font-light flex items-start gap-2">
                        <span className="text-[#C9A961] mt-1">·</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── RESPONSIBLE INVESTING + BELIEF + IDENTITY ───────────── */}
      <section data-testid="philosophy-section" className="py-24 lg:py-32 relative">
        <div className="container-myc grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Sustainable, responsible investing"
              className="w-full object-cover h-[560px] shadow-2xl"
            />
            <div className="mt-8 max-w-md">
              <Leaf className="w-6 h-6 text-[#7A1F26]" />
              <h3 className="font-display text-2xl text-[#2A0A0E] mt-4 font-medium italic">Responsible Investing</h3>
              <p className="text-[15px] text-[#2A0A0E]/75 leading-[1.7] mt-3 font-light">
                As a disciplined capital allocator, MONEYYARD is committed to advancing sustainable finance through the integration of sustainability-led investment principles — setting a benchmark for responsible stewardship in India's wealth advisory ecosystem.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-14">
            <div>
              <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Belief</p>
              <h3 className="font-display text-3xl lg:text-4xl text-[#2A0A0E] mt-4 font-light leading-tight">
                Wealth <span className="italic text-[#7A1F26]">transcends</span> financial capital.
              </h3>
              <p className="text-[17px] text-[#2A0A0E]/80 leading-[1.75] mt-5 font-light">
                It encompasses time, community, family legacy, and intergenerational responsibility. Through decades of advising families, we have developed deep expertise in preserving, nurturing, and extending the longevity of wealth across generations.
              </p>
            </div>

            <div className="border-t border-[#2A0A0E]/10 pt-14">
              <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Identity</p>
              <h3 className="font-display text-3xl lg:text-4xl text-[#2A0A0E] mt-4 font-light leading-tight">
                The bridge between <span className="italic text-[#7A1F26]">legacy</span> and reality.
              </h3>
              <p className="text-[17px] text-[#2A0A0E]/80 leading-[1.75] mt-5 font-light">
                Serving as the definitive bridge between your long-term legacy vision and present-day realities, MONEYYARD provides the institutional strength and strategic foundation upon which enduring relationships are built — steadfast, resilient, and future-focused.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── WAY WE SERVE (3 pillars) ───────────── */}
      <section data-testid="serve-section" className="py-24 lg:py-32 bg-[#2A0A0E] relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] bg-[#7A1F26]/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-[480px] h-[480px] bg-[#C9A961]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative container-myc">
          <div className="max-w-3xl">
            <p className="text-[#C9A961] text-[11px] font-medium tracking-[0.35em] uppercase">Our Values</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#FBF6EC] mt-6 font-light">
              Defining the way <span className="italic text-[#C9A961]">we serve</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#FBF6EC]/10 mt-16 border border-[#FBF6EC]/10">
            {[
              { icon: Heart, n: "01", t: "Compassion", d: "When clients engage with us, we begin by developing a deep understanding of their needs. Every concern is carefully heard and addressed with considered, thoughtful responses." },
              { icon: Sparkles, n: "02", t: "Social Responsibility", d: "With a tradition of thinking and acting on the principles of social responsibility, sustainability, and philanthropy at the heart of every recommendation." },
              { icon: Lightbulb, n: "03", t: "Technological Innovation", d: "Armed with the ability and drive to relentlessly rethink everything, we deploy technological innovation at every forefront of our advisory practice." },
            ].map(({ icon: Icon, n, t, d }) => (
              <div
                key={t}
                data-testid={`serve-card-${n}`}
                className="bg-[#2A0A0E] p-10 group hover:bg-[#3F0A0E] transition-colors"
              >
                <p className="font-display text-[#C9A961]/80 text-xl italic">{n}</p>
                <Icon className="w-8 h-8 text-[#C9A961] mt-6" />
                <h3 className="font-display text-2xl lg:text-3xl text-[#FBF6EC] mt-6 font-medium">{t}</h3>
                <p className="text-[15px] text-[#FBF6EC]/75 leading-[1.7] mt-4 font-light">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── FAMILY OFFICE OFFERINGS ───────────── */}
      <section data-testid="family-office-section" className="py-24 lg:py-32 relative">
        <div className="container-myc">
          <div className="max-w-3xl">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Family Office Offerings</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              Extending the life cycle of your <span className="italic text-[#7A1F26]">family's wealth</span>.
            </h2>
            <p className="text-[17px] text-[#2A0A0E]/80 leading-[1.75] font-light mt-6">
              Our services are structured around two core objectives: preserving family wealth and managing family assets. All advice is strictly merit-based, independent, and free from any conflicts of interest.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-16">
            {[
              {
                icon: TrendingUp,
                t: "Public Market",
                d: "We assist you with products that match your risk profile and lay a foundation in your portfolio to deliver alpha. Our technology-led portfolio analytics give you a full picture of your investments in a nutshell.",
                img: "https://images.unsplash.com/photo-1535320903710-d993d3d77d29?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
              },
              {
                icon: Network,
                t: "Private Market",
                d: "Our alternative investment capabilities leverage strong relationships with high-performing private enterprises and top-tier private equity and venture capital funds, enabling differentiated, high-conviction opportunities.",
                img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
              },
              {
                icon: BarChart3,
                t: "Reporting & Analytics",
                d: "Real-time, 24/7 portfolio insights via our mobile application alongside detailed monthly performance and risk reports — enabling clients to actively understand and engage with their portfolios.",
                img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
              },
              {
                icon: Building2,
                t: "Real Estate",
                d: "Periodic valuations of clients' real estate holdings integrated with their broader financial portfolios. Our asset management services ensure comprehensive, end-to-end oversight of real estate assets.",
                img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
              },
            ].map((o, i) => (
              <div
                key={o.t}
                data-testid={`family-offering-${i}`}
                className="group bg-[#FAF0DC] hover:bg-[#F5E8CC] transition-colors overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={o.img}
                    alt={o.t}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                  />
                </div>
                <div className="p-8 lg:p-10">
                  <o.icon className="w-6 h-6 text-[#7A1F26]" />
                  <h3 className="font-display text-2xl lg:text-3xl text-[#2A0A0E] mt-5 font-medium">{o.t}</h3>
                  <p className="text-[15px] text-[#2A0A0E]/75 leading-[1.7] mt-4 font-light">{o.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── LEGACY VISION (3 cards) ───────────── */}
      <section data-testid="legacy-section" className="py-24 lg:py-32 bg-[#FAF0DC] relative">
        <div className="container-myc">
          <div className="max-w-3xl">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Estate · Succession · Trust</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              Vision to shape a <span className="italic text-[#7A1F26]">great legacy</span>.
            </h2>
            <p className="text-[17px] text-[#2A0A0E]/80 leading-[1.75] font-light mt-6">
              We enable the transformation of hard-earned wealth into a lasting multi-generational legacy through our internationally recognised, impartial, and fully independent investment advisory.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: ScrollText, t: "Estate Plan & Wealth Transfer", d: "Private trusts offer a robust mechanism to safeguard and insulate assets from third-party claims, while enabling strategic planning for potential future inheritance tax and estate duty frameworks in India." },
              { icon: FileSignature, t: "Will & Executorship", d: "Managing the complex responsibilities of an executor during an emotionally demanding period can be challenging. Our experienced professionals work toward a singular objective: building and preserving enduring legacies." },
              { icon: Landmark, t: "Trust & Administration", d: "Trusts often form a critical component of intergenerational succession planning. We collaborate with qualified trust officers and administrators skilled in domestic and international jurisdictions." },
            ].map((c, i) => (
              <div
                key={c.t}
                data-testid={`legacy-card-${i}`}
                className="bg-[#FBF6EC] p-9 lg:p-10 hover:shadow-xl transition-shadow border border-[#2A0A0E]/5"
              >
                <c.icon className="w-7 h-7 text-[#7A1F26]" />
                <h3 className="font-display text-2xl text-[#2A0A0E] mt-6 font-medium leading-tight">{c.t}</h3>
                <p className="text-[15px] text-[#2A0A0E]/75 leading-[1.7] mt-4 font-light">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── FOUNDER QUOTE / OUR STORY ───────────── */}
      <section data-testid="quote-section" className="py-24 lg:py-32 relative bg-white">
        <div className="container-myc">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-[#7A1F26] text-[11px] font-medium tracking-[0.35em] uppercase">Our Story</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-[1.08] text-[#2A0A0E] mt-6 font-light">
              <span className="italic text-[#7A1F26]">Walking alongside</span> our clients.
            </h2>
          </div>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4">
              <img
                src="/bridge.png"
                alt="Bridging legacy and reality"
                className="w-full object-cover h-[320px] lg:h-[380px]"
              />
            </div>
            <div className="lg:col-span-8 lg:pl-4">
              <Quote className="w-12 h-12 text-[#7A1F26]/30" />
              <blockquote className="font-display text-2xl sm:text-3xl lg:text-[32px] text-[#2A0A0E] mt-6 leading-[1.45] font-light italic">
                &ldquo;The hallmark of our offering is professional and deeply personalised wealth advisory. Our clients rely on us to walk alongside them on important and deeply personal matters around their financial well-being.&rdquo;
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── CTA ───────────── */}
      <section
        data-testid="cta-section"
        className="relative py-24 lg:py-32 overflow-hidden bg-[#2A0A0E]"
      >
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] bg-[#7A1F26]/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-20 w-[480px] h-[480px] bg-[#C9A961]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative container-myc text-center max-w-3xl">
          <p className="text-[11px] tracking-[0.3em] uppercase font-medium text-[#C9A961]">Your Legacy Awaits</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[52px] leading-tight text-[#FBF6EC] mt-6 font-light">
            A <span className="italic text-[#C9A961]">conversation</span> worth having.
          </h2>
          <p className="text-[#FBF6EC]/75 mt-6 text-base sm:text-lg leading-relaxed font-light">
            Thirty minutes with a senior advisor can clarify a decade of decisions. Confidential. No obligation.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <ConsultationDialog
              trigger={
                <button
                  data-testid="cta-book-consultation-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#C9A961] text-[#2A0A0E] hover:bg-[#FBF6EC] transition-all duration-300 px-8 py-4 font-medium text-sm tracking-[0.2em] uppercase"
                >
                  Book a Consultation
                  <ArrowRight className="w-4 h-4" />
                </button>
              }
            />
            <Link
              to="/services"
              data-testid="cta-explore-services-link"
              className="inline-flex items-center justify-center gap-2 border border-[#FBF6EC]/40 text-[#FBF6EC] hover:bg-[#FBF6EC] hover:text-[#2A0A0E] transition-all duration-300 px-8 py-4 font-medium text-sm tracking-[0.2em] uppercase"
            >
              Explore Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
